import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, message, Button } from 'antd';
import type { Coach, Club } from '@/types/models';
import type { Coach as CoachEntity } from '@/types/entities';
import { CoachService } from '@/services/coach.service';
// import { ClubService } from '@/services/club.service';
import apiService from '@/services/api.service';
import { logger } from '@/utils/logger';
import { useAuth } from '@/contexts/AuthContext';

interface CreateCoachModalProps {
    visible: boolean;
    coach?: Coach | null;
    onCancel: () => void;
    onSuccess: () => void;
    clubId?: number; // Опциональный ID клуба для автоматического назначения
}

export const CreateCoachModal: React.FC<CreateCoachModalProps> = ({
    visible,
    coach,
    onCancel,
    onSuccess,
    clubId,
}) => {
    const [form] = Form.useForm();
    const isEditing = !!coach;
    const [loading, setLoading] = useState(false);
    const [clubs, setClubs] = useState<Club[]>([]);
    const [loadingClubs, setLoadingClubs] = useState(false);
    const [currentCoach, setCurrentCoach] = useState<CoachEntity | null>(null);
    const { user } = useAuth(); // Получаем информацию о текущем пользователе

    const handleSubmit = async (values: any) => {
        try {
            setLoading(true);

            // Убеждаемся, что phoneNumber передается корректно
            const submitData = {
                ...values,
                phoneNumber: values.phoneNumber || values.phone || ''
            };
            
            // Удаляем поле phone, если оно есть, чтобы избежать конфликта
            delete submitData.phone;

            if (isEditing && (currentCoach || coach)) {
                const coachToUpdate = currentCoach || coach;
                await CoachService.updateCoach(coachToUpdate!.id, submitData);
                message.success('Тренер успешно обновлен');
            } else {
                await CoachService.createCoach(submitData);
                message.success('Тренер успешно создан');
            }

            form.resetFields();
            onSuccess();
        } catch (error) {
            logger.error('Error submitting coach form:', error);
            message.error('Ошибка при сохранении данных тренера');
        } finally {
            setLoading(false);
        }
    };

    // Функция для загрузки списка клубов
    const fetchClubs = async () => {
        try {
            setLoadingClubs(true);            

            // Используем напрямую apiService для загрузки клубов
            const { data } = await apiService.get(
                '/clubs',
                {
                    params: {
                        page: 1,
                        limit: 100
                    }
                }
            );            

            if (data) {
                const responseData = data as any;
                if (responseData.data && Array.isArray(responseData.data)) {                    
                    setClubs(responseData.data);
                } else if (responseData.data && responseData.data.rows) {                    
                    setClubs(responseData.data.rows);
                } else {
                    logger.warn('No clubs received or invalid response format');
                    setClubs([]);
                }
            } else {
                logger.warn('No data received');
                setClubs([]);
            }
        } catch (error) {
            logger.error('Error fetching clubs:', error);
            message.error('Ошибка при загрузке списка клубов');
        } finally {
            setLoadingClubs(false);
        }
    };

    // Загружаем список клубов при монтировании компонента и при открытии модального окна
    useEffect(() => {
        // Загружаем список клубов при монтировании компонента
        fetchClubs();
    }, []); // Пустой массив зависимостей означает, что эффект будет выполнен только при монтировании

    // Загрузка актуальных данных тренера из БД при открытии модального окна
    const fetchCoachData = async () => {
        if (!coach?.id) return;

        try {            
            // Загружаем актуальные данные тренера из БД
            const coachData = await CoachService.getCoachById(coach.id);
            
            if (coachData) {
                setCurrentCoach(coachData);                
            } else {
                logger.warn('Не удалось загрузить данные тренера, используем переданные данные');
                setCurrentCoach(coach);
            }
        } catch (error) {
            logger.error('Ошибка при загрузке данных тренера:', error);
            // В случае ошибки используем переданные данные
            setCurrentCoach(coach);
        }
    };

    // Загружаем список клубов при открытии модального окна
    useEffect(() => {
        if (visible) {
            fetchClubs();
            // Если редактируем тренера, загружаем актуальные данные из БД
            if (coach?.id) {
                fetchCoachData();
            } else {
                setCurrentCoach(null);
            }
        } else {
            setCurrentCoach(null);
        }
    }, [visible, coach?.id]);

    // Заполняем форму при редактировании
    useEffect(() => {
        // Работаем с формой только когда модальное окно открыто
        if (!visible) {
            return;
        }

        // Используем актуальные данные из БД, если они загружены, иначе используем переданные данные
        const coachToUse = currentCoach || coach;

        if (coachToUse) {
            // Редактирование существующего тренера
            // Определяем clubId: сначала из объекта тренера, потом из club, потом из пропсов
            const coachClubId = (coachToUse as any).clubId || (coachToUse as any).club?.id || clubId;
            
            // Определяем phoneNumber: сначала из user.phoneNumber, потом из phoneNumber, потом из phone
            const coachPhoneNumber = (coachToUse as any).user?.phoneNumber || 
                                    (coachToUse as any).phoneNumber || 
                                    (coachToUse as any).phone || 
                                    '';
            
            // Небольшая задержка для корректной инициализации формы
            setTimeout(() => {
                form.setFieldsValue({
                    firstName: (coachToUse as any).firstName || (coachToUse as any).user?.firstName,
                    lastName: (coachToUse as any).lastName || (coachToUse as any).user?.lastName,
                    middleName: (coachToUse as any).middleName || (coachToUse as any).user?.middleName,
                    email: (coachToUse as any).email || (coachToUse as any).user?.email,
                    phoneNumber: coachPhoneNumber,
                    status: (coachToUse as any).status,
                    clubId: coachClubId
                });
            }, 100);
        } else {
            // Создание нового тренера - сначала сбрасываем, потом устанавливаем значения
            form.resetFields();

            // Если передан clubId, устанавливаем его
            if (clubId) {
                form.setFieldsValue({ clubId });
            }
            // Если пользователь - менеджер, устанавливаем его клуб по умолчанию (если clubId не передан)
            else if (user && (user.role === 'manager') && user.clubId) {
                form.setFieldsValue({ clubId: user.clubId });
            }
        }
    }, [visible, currentCoach, coach, form, user, clubId]);

    return (
        <Modal
            title={isEditing ? 'Редактировать тренера' : 'Добавить тренера'}
            open={visible}
            onCancel={onCancel}
            okText={isEditing ? 'Сохранить' : 'Создать'}
            cancelText="Отмена"
            confirmLoading={loading}
            // Remove onOk prop and let Form handle submission
            footer={null}
            destroyOnClose={true}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    status: 'active'
                }}
            >
                <Form.Item
                    name="lastName"
                    label="Фамилия"
                    rules={[{ required: true, message: 'Введите фамилию' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="firstName"
                    label="Имя"
                    rules={[{ required: true, message: 'Введите имя' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="middleName"
                    label="Отчество"
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                        { required: true, message: 'Введите email' },
                        { type: 'email', message: 'Введите корректный email' }
                    ]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="phoneNumber"
                    label="Телефон"
                    rules={[{ required: true, message: 'Введите телефон' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="clubId"
                    label={<div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <span>Клуб</span>
                        <Button
                            type="link"
                            size="small"
                            onClick={(e) => {
                                e.preventDefault();
                                fetchClubs();
                            }}
                            loading={loadingClubs}
                        >
                            Обновить
                        </Button>
                    </div>}
                    rules={[{ required: true, message: 'Выберите клуб' }]}
                >
                    <Select
                        loading={loadingClubs}
                        placeholder="Выберите клуб"
                        disabled={user?.role === 'manager' || !!clubId}
                        notFoundContent={clubs.length === 0 ? 'Нет доступных клубов' : null}
                        showSearch
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                    >
                        {clubs.length > 0 ? (
                            clubs.map(club => (
                                <Select.Option key={club.id} value={club.id}>
                                    {club.name}
                                </Select.Option>
                            ))
                        ) : (
                            <Select.Option value="" disabled>
                                Нет доступных клубов
                            </Select.Option>
                        )}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="status"
                    label="Статус"
                    rules={[{ required: true, message: 'Выберите статус' }]}
                >
                    <Select>
                        <Select.Option value="active">Активен</Select.Option>
                        <Select.Option value="inactive">Неактивен</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item className="mb-0 text-right">
                    <Button onClick={onCancel} style={{ marginRight: 8 }}>
                        Отмена
                    </Button>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {isEditing ? 'Сохранить' : 'Создать'}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};